/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Gift, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { getOrientationStatus, consumeOrientation } from '@/lib/growth';
import { useNavigate } from 'react-router-dom';

/**
 * Patient home hero card offering the 2 free monthly orientation consults.
 * Uses framer-motion for a soft entry animation.
 */
export const OrientationHeroCard = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { (async () => {
    try { setState(await getOrientationStatus()); } catch { setState(null); }
    finally { setLoading(false); }
  })(); }, []);

  const claim = async () => {
    setClaiming(true);
    try {
      const res = await consumeOrientation('Consulto di orientamento gratuito');
      toast.success(`Consulto attivato · ${res.remaining} rimanent${res.remaining === 1 ? 'e' : 'i'} questo mese`);
      setState((s) => ({ ...(s || {}), used: (s?.used || 0) + 1, remaining: res.remaining }));
      navigate('/telemedicine');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile attivare');
    } finally { setClaiming(false); }
  };

  if (loading || !state) return null;
  const remaining = state.remaining ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      data-testid="orientation-hero"
    >
      <Card className="overflow-hidden border-primary/40 bg-gradient-to-br from-primary/10 via-background to-emerald-500/10">
        <CardContent className="p-5 flex items-center gap-4 flex-wrap">
          <motion.div
            initial={{ scale: 0.9, rotate: -6 }} animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18, delay: 0.1 }}
            className="h-14 w-14 rounded-2xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0"
          >
            <Gift className="h-7 w-7" />
          </motion.div>
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-lg font-semibold">2 consulti di orientamento gratuiti al mese</h2>
              <Badge variant="secondary" data-testid="orientation-remaining">{remaining} rimanent{remaining === 1 ? 'e' : 'i'}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Parla con un medico dopo un breve triage AI — zero code, zero costi, per capire se serve una visita specialistica.
            </p>
          </div>
          <Button onClick={claim} disabled={claiming || remaining <= 0} data-testid="orientation-claim" className="gap-1">
            <Sparkles className="h-4 w-4" />
            {remaining > 0 ? 'Attiva ora' : 'Esaurito questo mese'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default OrientationHeroCard;
